"use client";
import React from "react";
import { Box } from "@mui/material";
import SvgIcon, { SvgIconProps } from "@mui/material/SvgIcon";
import { SxProps, Theme } from "@mui/material/styles";

import GoogleIcon from "@mui/icons-material/Google";
import FacebookIcon from "@mui/icons-material/FacebookRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PinterestIcon from "@mui/icons-material/Pinterest";
import BlogIcon from "@mui/icons-material/RssFeedRounded";

export type SocialPlatform =
  | "google"
  | "twitter"
  | "facebook"
  | "instagram"
  | "tiktok"
  | "youtube"
  | "pinterest"
  | "linkedin"
  | "blog";

export type SocialLinks = Partial<Record<SocialPlatform, string>>;

const DEFAULT_SOCIAL_LINKS: SocialLinks = {
  google: "https://share.google/ZVQLuZ35B5XwOIeIi",
  twitter: "https://x.com/w_cruise_vibes",
  facebook: "https://web.facebook.com/worldcruisevibes",
  instagram: "#",
  tiktok: "#",
  youtube: "#",
  pinterest: "#",
  linkedin: "#",
  blog: "#",
};

type SocialIconsProps = {
  sx?: SxProps<Theme>;
  links?: SocialLinks;
  socialPlatform?: string[];
};

const SocialIcons: React.FC<SocialIconsProps> = ({
  sx = [],
  links,
  socialPlatform = [
    "google",
    "twitter",
    "facebook",
    "instagram",
    "tiktok",
    "youtube",
    "pinterest",
    "linkedin",
    "blog",
  ],
}) => {
  const resolvedLinks = links ?? DEFAULT_SOCIAL_LINKS;

  return (
    <Box
      sx={{
        display: "flex",
        gap: { xs: "7px", sm: "10px" },
        ".social_icon_wrapper": {
          color: "#000C37",
          bgcolor: "#fff",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "50%",
          p: { xs: "4px", sm: "5px" },
          svg: {
            display: "flex",
            width: { xs: "24px", sm: "28px", md: "30px", lg: "26px" },
            height: { xs: "24px", sm: "28px", md: "30px", lg: "26px" },
            fill: "current.color",
          },
          transition: "all 0.5s",
          "&:hover": {
            cursor: "pointer",
            bgcolor: "#5900FF",
            color: "#fff",
            svg: {
              fill: "current.color",
            },
          },
        },
        ...sx,
      }}
    >
      {SOCIAL_PLATFORMS.filter(({ key }) => socialPlatform.includes(key)).map(
        ({ key, label, icon: Icon, rel, sx: iconSx }) => {
          const href = resolvedLinks[key];

          if (!href) return null;

          return (
            <Box
              key={key}
              className="social_icon_wrapper"
              component="a"
              href={href}
              target="_blank"
              rel={rel}
              aria-label={label}
              sx={iconSx}
            >
              <Icon />
            </Box>
          );
        },
      )}
    </Box>
  );
};

export default React.memo(SocialIcons);

export const TiktokIcon = React.memo((props: SvgIconProps) => {
  return (
    <SvgIcon {...props}>
      <g>
        <rect fill="none" height="24" width="24" y="0" />
      </g>
      <g>
        <path d="M16.6,5.82C16.6,5.82,17.11,6.32,16.6,5.82C15.94,5.06,15.54,4.08,15.54,3h-3.09l0,12.4c-0.05,1.39-1.19,2.5-2.59,2.5 c-1.42,0-2.6-1.16-2.6-2.6c0-1.72,1.66-3.01,3.37-2.48V9.66C7.18,9.2,4.16,11.88,4.16,15.3c0,3.33,2.76,5.7,5.69,5.7 c3.14,0,5.69-2.55,5.69-5.7V9.01c1.21,0.87,2.7,1.38,4.3,1.38V7.3C19.84,7.3,17.96,7.39,16.6,5.82z" />
      </g>
    </SvgIcon>
  );
});

export const TwiterXIcon = React.memo((props: SvgIconProps) => {
  return (
    <SvgIcon
      width="104"
      height="97"
      viewBox="0 0 104 97"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M45.8898 49.0798L49.5198 53.7198L45.5398 58.0698L10.8398 96.0298H2.96979L41.9198 53.4198L45.8898 49.0798Z"
        fill="currentColor"
      />
      <path
        d="M98.1598 0.509766L59.2198 43.1098L55.2498 47.4498L51.6298 42.8098L55.5998 38.4698L90.2998 0.509766H98.1598Z"
        fill="currentColor"
      />
      <path d="M100.57 96.0198V96.1698H75.2398L75.1298 96.0198H100.57Z" fill="currentColor" />
      <path
        d="M100.46 96.0299L59.2198 43.1099L55.5998 38.4699L25.9098 0.369873H0.569763L41.9198 53.4199L45.5398 58.0699L75.1298 96.0299L75.2398 96.1799H100.57L100.46 96.0299ZM49.5198 53.7199L45.8898 49.0699L12.4498 6.16987H23.0698L51.6198 42.8099L55.2398 47.4499L88.6898 90.3699H78.0698L49.5198 53.7199Z"
        fill="currentColor"
      />
      <path
        d="M45.8898 49.0798L49.5198 53.7198L45.5398 58.0698L10.8398 96.0298H2.96979L41.9198 53.4198L45.8898 49.0798Z"
        fill="currentColor"
      />
      <path
        d="M98.1598 0.509766L59.2198 43.1098L55.2498 47.4498L51.6298 42.8098L55.5998 38.4698L90.2998 0.509766H98.1598Z"
        fill="currentColor"
      />
      <path d="M100.57 96.0198V96.1698H75.2398L75.1298 96.0198H100.57Z" fill="currentColor" />
      <path
        d="M100.46 96.0299L59.2198 43.1099L55.5998 38.4699L25.9098 0.369873H0.569763L41.9198 53.4199L45.5398 58.0699L75.1298 96.0299L75.2398 96.1799H100.57L100.46 96.0299ZM49.5198 53.7199L45.8898 49.0699L12.4498 6.16987H23.0698L51.6198 42.8099L55.2398 47.4499L88.6898 90.3699H78.0698L49.5198 53.7199Z"
        fill="currentColor"
      />
      <path
        d="M49.2653 49.0798L52.8953 53.7198L48.9153 58.0698L14.2153 96.0298H6.34534L45.2953 53.4198L49.2653 49.0798Z"
        fill="currentColor"
      />
      <path
        d="M101.535 0.509766L62.5953 43.1098L58.6253 47.4498L55.0053 42.8098L58.9753 38.4698L93.6753 0.509766H101.535Z"
        fill="currentColor"
      />
      <path d="M103.945 96.0198V96.1698H78.6153L78.5053 96.0198H103.945Z" fill="currentColor" />
      <path
        d="M103.835 96.0299L62.5953 43.1099L58.9753 38.4699L29.2853 0.369873H3.94531L45.2953 53.4199L48.9153 58.0699L78.5053 96.0299L78.6153 96.1799H103.945L103.835 96.0299ZM52.8953 53.7199L49.2653 49.0699L15.8253 6.16987H26.4453L54.9953 42.8099L58.6153 47.4499L92.0653 90.3699H81.4453L52.8953 53.7199Z"
        fill="currentColor"
      />
    </SvgIcon>
  );
});

export const MessengerIcon = React.memo((props: SvgIconProps) => {
  return (
    <SvgIcon
      {...props}
      width="45"
      height="41"
      viewBox="0 0 54 51"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.02307 38.0424C9.02025 36.3458 10.9982 35.4165 12.3023 36.5017L20.5136 43.3344C21.639 44.2709 21.4099 46.0594 20.0849 46.682L11.8913 50.5315C10.5662 51.1541 9.04324 50.1887 9.04081 48.7247L9.02307 38.0424Z"
        fill="currentColor"
      />
      <path
        d="M54 24.5C54 38.031 41.9117 49 27 49C12.0883 49 0 38.031 0 24.5C0 10.969 12.0883 0 27 0C41.9117 0 54 10.969 54 24.5Z"
        fill="currentColor"
      />
      <path
        d="M14.1646 30.4339L20.4427 20.4541C21.4409 18.8646 23.58 18.4742 25.0791 19.5967L30.0773 23.3508C30.5364 23.6924 31.1659 23.6924 31.6216 23.3438L38.3623 18.2163C39.2597 17.5296 40.4353 18.6137 39.837 19.5723L33.552 29.545C32.5537 31.1345 30.4146 31.5249 28.9156 30.4025L23.9209 26.6483C23.4618 26.3067 22.8322 26.3067 22.3766 26.6553L15.6359 31.7828C14.7385 32.4695 13.5629 31.3924 14.1646 30.4339Z"
        fill="#ffffff"
      />
    </SvgIcon>
  );
});

export const InfoIcon = React.memo((props: SvgIconProps) => {
  return (
    <SvgIcon
      {...props}
      id="Layer_1"
      data-name="Layer 1"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 122.88 122.88"
    >
      <path d="M61.44,0A61.46,61.46,0,1,1,18,18,61.25,61.25,0,0,1,61.44,0ZM59.12,36a8,8,0,0,1,.61-3.16,7.82,7.82,0,0,1,1.8-2.63,8.33,8.33,0,0,1,2.62-1.79,8.08,8.08,0,0,1,6.11,0,8.06,8.06,0,0,1,2.58,1.79,7.83,7.83,0,0,1,1.77,2.63A8.38,8.38,0,0,1,75.2,36a8.15,8.15,0,0,1-.59,3.1,8.23,8.23,0,0,1-1.76,2.65,8.15,8.15,0,0,1-2.59,1.82,7.72,7.72,0,0,1-3.05.6,8,8,0,0,1-3.12-.6,7.84,7.84,0,0,1-2.61-1.8,8.07,8.07,0,0,1-1.77-2.64A8.3,8.3,0,0,1,59.12,36Zm3.09,47.8-.17.63-.12.49-.05.34,0,.27a2,2,0,0,0,.09.64v0a1.09,1.09,0,0,0,.23.41.86.86,0,0,0,.35.23,1.55,1.55,0,0,0,.55.09,2.74,2.74,0,0,0,1.46-.63,14.6,14.6,0,0,0,2.15-2.06,36,36,0,0,0,2.57-3.3c.89-1.26,1.82-2.71,2.79-4.33a.37.37,0,0,1,.5-.13l3.28,2.44a.36.36,0,0,1,.09.5,56.84,56.84,0,0,1-4.58,6.87,30.35,30.35,0,0,1-4.73,4.89l0,0a18.55,18.55,0,0,1-4.92,2.92,14.15,14.15,0,0,1-5.19,1,13.63,13.63,0,0,1-4.07-.55,7.92,7.92,0,0,1-3-1.66,7.1,7.1,0,0,1-1.86-2.72,9.92,9.92,0,0,1-.61-3.62c0-.45,0-.92.08-1.42s.14-1,.25-1.58v0c.1-.54.25-1.15.43-1.82s.41-1.43.67-2.26L54.1,61.61l.47-1.67c.12-.47.22-.88.3-1.24a8.43,8.43,0,0,0,.15-.9,5.75,5.75,0,0,0,.06-.77,2.9,2.9,0,0,0-.2-1.09v0a2.49,2.49,0,0,0-.57-.81,2.68,2.68,0,0,0-.94-.55,4.15,4.15,0,0,0-1.28-.19H47.45a.37.37,0,0,1-.37-.36l0-.13,1.22-4.43a.37.37,0,0,1,.36-.27l23.67-.75a.38.38,0,0,1,.38.36l0,.12L62.21,83.78ZM97,25.88a50.31,50.31,0,1,0,14.72,35.56A50.16,50.16,0,0,0,97,25.88Z" />
    </SvgIcon>
  );
});

export const InfoIconV2 = React.memo((props: SvgIconProps) => {
  return (
    <SvgIcon
      id="info_icons"
      data-name="info_icons"
      xmlns="http://www.w3.org/2000/svg"
      height="24"
      width="24"
      viewBox="0 -960 960 960"
      {...props}
    >
      <path d="M480-280q17 0 28.5-11.5T520-320v-160q0-17-11.5-28.5T480-520q-17 0-28.5 11.5T440-480v160q0 17 11.5 28.5T480-280Zm0-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
    </SvgIcon>
  );
});

TiktokIcon.displayName = "TiktokIcon";
MessengerIcon.displayName = "MessengerIcon";
InfoIcon.displayName = "InfoIcon";
InfoIconV2.displayName = "InfoIconV2";
TwiterXIcon.displayName = "TwiterXIcon";

const SOCIAL_PLATFORMS: {
  key: SocialPlatform;
  label: string;
  icon: React.ComponentType<SvgIconProps>;
  rel: string;
  sx?: SxProps<Theme>;
}[] = [
  { key: "google", label: "Google", icon: GoogleIcon, rel: "nofollow noreferrer" },
  {
    key: "twitter",
    label: "X (Twitter)",
    icon: TwiterXIcon,
    rel: "nofollow noreferrer",
    sx: { svg: { p: "5px" } },
  },
  { key: "facebook", label: "Facebook", icon: FacebookIcon, rel: "nofollow noreferrer" },
  { key: "instagram", label: "Instagram", icon: InstagramIcon, rel: "nofollow noreferrer" },
  { key: "tiktok", label: "TikTok", icon: TiktokIcon, rel: "noreferrer" },
  { key: "youtube", label: "YouTube", icon: YouTubeIcon, rel: "nofollow noreferrer" },
  { key: "pinterest", label: "Pinterest", icon: PinterestIcon, rel: "nofollow noreferrer" },
  { key: "linkedin", label: "LinkedIn", icon: LinkedInIcon, rel: "nofollow noreferrer" },
  { key: "blog", label: "Blog", icon: BlogIcon, rel: "noreferrer" },
];
